import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { adminAPI } from '../utils/api';
import * as XLSX from 'xlsx';
import {
  X,
  Upload,
  FileSpreadsheet,
  Download,
  Users,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
} from 'lucide-react';

/**
 * Bulk Import Modal Component
 * Allows admins to import multiple users from an Excel file
 */
const BulkImportModal = ({ onClose, onImportComplete }) => {
  const [loading, setLoading] = useState(false);
  const [parsedUsers, setParsedUsers] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [step, setStep] = useState('upload'); // upload, preview, results
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(sheet);

        // Map Excel columns to user fields
        const users = data.map((row, index) => ({
          _rowNum: index + 2, // For display (accounting for header row)
          email: row['Email'] || row['email'] || '',
          name: row['Name'] || row['Full Name'] || row['name'] || '',
          role: (row['Role'] || row['role'] || 'technician').toLowerCase(),
          phone: row['Phone'] || row['phone'] || '',
          employee_id: row['Employee ID'] || row['employee_id'] || '',
          id_number: row['ID Number'] || row['id_number'] || '',
          address: row['Address'] || row['address'] || '',
          emergency_contact: row['Emergency Contact'] || row['emergency_contact'] || '',
          emergency_contact_phone: row['Emergency Phone'] || row['emergency_contact_phone'] || '',
          hourly_rate: parseFloat(row['Hourly Rate'] || row['hourly_rate'] || 0) || 0,
          job_description: row['Job Description'] || row['job_description'] || '',
          send_welcome_email: true,
          _isValid: !!(row['Email'] || row['email']) && !!(row['Name'] || row['Full Name'] || row['name']),
        }));

        setParsedUsers(users);
        setStep('preview');
      } catch (error) {
        toast.error('Failed to parse Excel file. Please check the format.');
        console.error(error);
      }
    };
    reader.readAsBinaryString(file);
  };

  const downloadTemplate = () => {
    const template = [
      {
        'Email': 'john@example.com',
        'Name': 'John Doe',
        'Role': 'technician',
        'Phone': '+27 12 345 6789',
        'Employee ID': 'EMP001',
        'ID Number': '1234567890123',
        'Address': '123 Main Street, City',
        'Emergency Contact': 'Jane Doe',
        'Emergency Phone': '+27 12 345 6789',
        'Hourly Rate': 150,
        'Job Description': 'Roofing Specialist',
      },
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, 'user_import_template.xlsx');
  };

  const handleImport = async () => {
    const validUsers = parsedUsers.filter(u => u._isValid);
    
    if (validUsers.length === 0) {
      toast.error('No valid users to import');
      return;
    }

    setLoading(true);
    try {
      // Remove internal fields before sending
      const usersToImport = validUsers.map(({ _rowNum, _isValid, ...user }) => user);
      const response = await adminAPI.bulkImportUsers(usersToImport);
      setImportResults(response.data);
      setStep('results');
      if (onImportComplete) {
        onImportComplete(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import users');
    } finally {
      setLoading(false);
    }
  };

  const validCount = parsedUsers.filter(u => u._isValid).length;
  const invalidCount = parsedUsers.filter(u => !u._isValid).length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Bulk Import Users</h2>
              <p className="text-sm text-slate-500">
                {step === 'upload' && 'Upload an Excel file to import multiple users'}
                {step === 'preview' && `${validCount} valid users ready to import`}
                {step === 'results' && 'Import completed'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              {/* Upload Area */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-slate-700 mb-1">
                  Click to upload Excel file
                </p>
                <p className="text-sm text-slate-500">
                  Supports .xlsx and .xls formats
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>

              {/* Download Template */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FileSpreadsheet className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-blue-800 mb-1">Need a template?</h3>
                    <p className="text-sm text-blue-700 mb-3">
                      Download our Excel template with the correct column headers for easy importing.
                    </p>
                    <button
                      onClick={downloadTemplate}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      <Download className="w-4 h-4" />
                      Download Template
                    </button>
                  </div>
                </div>
              </div>

              {/* Required Fields Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-medium text-slate-800 mb-2">Required Columns</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Email</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Name (or Full Name)</span>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Optional: Role, Phone, Employee ID, ID Number, Address, Emergency Contact, etc.
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && (
            <div className="space-y-4">
              {/* Stats */}
              <div className="flex gap-4 mb-4">
                <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{validCount}</p>
                  <p className="text-sm text-green-600">Valid Users</p>
                </div>
                {invalidCount > 0 && (
                  <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-700">{invalidCount}</p>
                    <p className="text-sm text-red-600">Invalid (will skip)</p>
                  </div>
                )}
              </div>

              {/* User List */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Row</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Status</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Email</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Name</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Role</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {parsedUsers.map((user, idx) => (
                      <tr key={idx} className={!user._isValid ? 'bg-red-50' : ''}>
                        <td className="px-3 py-2 text-slate-500">{user._rowNum}</td>
                        <td className="px-3 py-2">
                          {user._isValid ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                        </td>
                        <td className="px-3 py-2">{user.email || <span className="text-red-600">Missing</span>}</td>
                        <td className="px-3 py-2">{user.name || <span className="text-red-600">Missing</span>}</td>
                        <td className="px-3 py-2 capitalize">{user.role}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> All users will be created with the default password and will be required to change it upon first login. Welcome emails will be sent to all imported users.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {step === 'results' && importResults && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-slate-700">{importResults.total}</p>
                  <p className="text-sm text-slate-500">Total Processed</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-700">{importResults.success?.length || 0}</p>
                  <p className="text-sm text-green-600">Successfully Created</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-red-700">{importResults.failed?.length || 0}</p>
                  <p className="text-sm text-red-600">Failed</p>
                </div>
              </div>

              {/* Success List */}
              {importResults.success?.length > 0 && (
                <div className="border border-green-200 rounded-lg overflow-hidden">
                  <div className="bg-green-50 px-4 py-2 border-b border-green-200">
                    <h3 className="font-medium text-green-800 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Successfully Created Users
                    </h3>
                  </div>
                  <div className="divide-y divide-green-100">
                    {importResults.success.map((user, idx) => (
                      <div key={idx} className="px-4 py-2 flex justify-between items-center">
                        <span className="text-sm">{user.name}</span>
                        <span className="text-sm text-slate-500">{user.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Failed List */}
              {importResults.failed?.length > 0 && (
                <div className="border border-red-200 rounded-lg overflow-hidden">
                  <div className="bg-red-50 px-4 py-2 border-b border-red-200">
                    <h3 className="font-medium text-red-800 flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      Failed to Import
                    </h3>
                  </div>
                  <div className="divide-y divide-red-100">
                    {importResults.failed.map((item, idx) => (
                      <div key={idx} className="px-4 py-2 flex justify-between items-center">
                        <span className="text-sm">{item.email}</span>
                        <span className="text-sm text-red-600">{item.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 bg-white border-t border-slate-200 p-4 flex justify-between">
          {step === 'preview' && (
            <button
              onClick={() => {
                setParsedUsers([]);
                setStep('upload');
              }}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              Back
            </button>
          )}
          <div className="flex gap-3 ml-auto">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              {step === 'results' ? 'Close' : 'Cancel'}
            </button>
            {step === 'preview' && (
              <button
                onClick={handleImport}
                disabled={loading || validCount === 0}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-slate-300"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Import {validCount} Users
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;
